import { FC, useState, useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import LeopartLoader from '../../../shared/LeopartLoader/LeopartLoader';
import {
  Plus,
  Search,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
} from 'lucide-react';
import { useEndpoints } from '../../../hooks/useEndpoints';
import { useDebouncedValue } from '../../../hooks/useDebouncedValue';
import { useConfirmModal } from '../../../hooks/useConfirmModal/useConfirmModal';
import { IFieldWorkout } from '../../../models/field-programming';
import FieldWorkoutCard from './FieldWorkoutCard';
import FieldWorkoutDetailModal from '../FieldWorkoutDetailModal/FieldWorkoutDetailModal';
import FieldWorkoutBuilderModal from '../FieldWorkoutBuilderModal/FieldWorkoutBuilderModal';
import FieldAssignModal from '../FieldAssignModal/FieldAssignModal';
import NewFieldSessionModal from '../NewFieldSessionModal/NewFieldSessionModal';
import styles from './FieldWorkoutsTab.module.scss';

const FieldWorkoutsTab: FC = () => {
  const api = useEndpoints();
  const qc = useQueryClient();
  const { handleOpen: openConfirm } = useConfirmModal();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<IFieldWorkout | null>(null);
  const [viewing, setViewing] = useState<IFieldWorkout | null>(null);
  const [reassigning, setReassigning] = useState<IFieldWorkout | null>(null);
  // Bug 69f9ae9d — flow allineato a "Nuova sessione" del Dettaglio Team con
  // pre-step destinatario per la sezione principale Programmazione.
  const [newSessionOpen, setNewSessionOpen] = useState(false);
  const debouncedSearch = useDebouncedValue(search, 300);

  const workoutsQ = useQuery({
    queryKey: ['fieldProgramming', 'workouts', { search: debouncedSearch, page }],
    queryFn: () =>
      api.fieldProgramming.workouts
        .list({ search: debouncedSearch || undefined, page, limit: 20 })
        .then((r) => r.data),
    staleTime: 2 * 60 * 1000,
  });

  const dupMut = useMutation({
    mutationFn: (id: string) => api.fieldProgramming.workouts.duplicate(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fieldProgramming', 'workouts'] }),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => api.fieldProgramming.workouts.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fieldProgramming', 'workouts'] }),
  });

  const workouts = useMemo(() => workoutsQ.data?.docs ?? [], [workoutsQ.data]);
  const totalPages = workoutsQ.data?.totalPages ?? 1;
  const totalDocs = workoutsQ.data?.totalDocs ?? 0;

  const handleNew = useCallback(() => {
    // Bug 69f9ae9d — bottone "Nuova Scheda Campo" ora apre il flow allineato
    // a "Nuova sessione" del Dettaglio Team (pre-step destinatario + form base).
    // Il vecchio FieldWorkoutBuilderModal 4-step resta riservato a edit di
    // template libreria esistenti (entry "Modifica" sulle card).
    setNewSessionOpen(true);
  }, []);

  const handleBuilderClose = useCallback(() => {
    setCreating(false);
    setEditing(null);
  }, []);

  const handleDelete = useCallback(
    (workout: IFieldWorkout) => {
      openConfirm({
        title: 'Eliminare scheda?',
        description: `Stai per eliminare "${workout.name}". Le sessioni già assegnate restano nel calendario.`,
        onConfirm: () => deleteMut.mutateAsync(workout._id),
      });
    },
    [openConfirm, deleteMut],
  );

  return (
    <div className={styles.container}>
      {/* Filter bar */}
      <div className={styles.filterBar}>
        <div className={styles.filterPills}>
          <button className={`${styles.filterPill} ${styles.filterPillActive}`}>
            Tutte
            {totalDocs > 0 && <span className={styles.pillCount}>{totalDocs}</span>}
          </button>
        </div>
        <button className={styles.addBtn} onClick={handleNew}>
          <Plus size={15} strokeWidth={2.5} />
          Nuova Scheda Campo
        </button>
      </div>

      {/* Search */}
      <div className={styles.searchWrapper}>
        <Search className={styles.searchIcon} />
        <input
          type="text"
          placeholder="Cerca scheda per nome..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          className={styles.searchInput}
        />
      </div>

      {/* Content */}
      {workoutsQ.isLoading ? (
        <div className={styles.loadingState}>
          <LeopartLoader />
        </div>
      ) : workouts.length === 0 ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyIconWrap}>
            <ClipboardList size={24} strokeWidth={1.5} />
          </div>
          <p className={styles.emptyTitle}>Nessuna scheda Campo trovata</p>
          <p className={styles.emptySubtitle}>
            {search
              ? 'Prova a modificare i filtri di ricerca'
              : 'Crea la tua prima scheda Campo per iniziare'}
          </p>
        </div>
      ) : (
        <>
          <div className={styles.grid}>
            {workouts.map((w) => (
              <FieldWorkoutCard
                key={w._id}
                workout={w}
                onOpen={() => setViewing(w)}
                onEdit={() => setEditing(w)}
                onDuplicate={() => dupMut.mutate(w._id)}
                onReassign={() => setReassigning(w)}
                onDelete={() => handleDelete(w)}
              />
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className={styles.paginationRow}>
              <span className={styles.paginationInfo}>
                Pagina {page} di {totalPages}
              </span>
              <div className={styles.paginationButtons}>
                <button
                  className={styles.pageBtn}
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  <ChevronLeft size={16} />
                </button>
                <span className={styles.pageCounter}>
                  {page} / {totalPages}
                </span>
                <button
                  className={styles.pageBtn}
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      <FieldWorkoutDetailModal workout={viewing} onClose={() => setViewing(null)} />

      <FieldWorkoutBuilderModal
        isOpen={creating || !!editing}
        workout={editing}
        onClose={handleBuilderClose}
      />

      {reassigning && (
        <FieldAssignModal
          workoutId={reassigning._id}
          workoutName={reassigning.name}
          onClose={() => setReassigning(null)}
        />
      )}

      <NewFieldSessionModal
        isOpen={newSessionOpen}
        onClose={() => setNewSessionOpen(false)}
      />
    </div>
  );
};

export default FieldWorkoutsTab;
